import { Command } from "commander"
import { execa } from "execa"
import { z } from "zod"
import { confirm, input, password as passwordPrompt } from "@inquirer/prompts"
import { authenticateUser, checkAuthStatus } from "@/src/utils/auth"
import { colors } from "@/src/utils/colors"
import { handleError } from "@/src/utils/handle-error"
import { logger } from "@/src/utils/logger"
import { getPackageManager } from "@/src/utils/get-package-manager"
import { logoutUser } from "@/src/utils/auth"
import { spinner } from "@/src/utils/spinner"
import { getAuthToken } from "@/src/utils/package-manager-config"
import { tokenStorage } from "@/src/utils/bootstrap"

export const authOptionsSchema = z.object({
  cwd: z.string(),
  email: z.string().optional(),
  password: z.string().optional(),
  writeConfig: z.boolean().optional(),
  showMessage: z.boolean().default(true),
})

type AuthOptions = z.infer<typeof authOptionsSchema>
type PackageManager = "npm" | "yarn" | "pnpm" | "bun"

/**
 * Creates a themed input prompt with consistent styling
 */
const createThemedInput = (
  message: string,
  options: {
    required?: boolean
    validate?: (value: string) => boolean | string
  } = {}
) => {
  return input({
    message: colors.reset(message),
    required: options.required ?? true,
    validate:
      options.validate ??
      ((value: string) => (value ? true : "This field is required")),
    theme: {
      prefix: {
        done: colors.cyan("✔"),
        idle: "?",
      },
    },
  })
}

/**
 * Creates a themed password prompt with consistent styling
 */
const createThemedPassword = (
  message: string,
  options: {
    validate?: (value: string) => boolean | string
  } = {}
) => {
  return passwordPrompt({
    message: colors.reset(message),
    validate:
      options.validate ??
      ((value: string) => (value ? true : "This field is required")),
    mask: "*",
    theme: {
      prefix: {
        done: colors.cyan("✔"),
        idle: "?",
      },
    },
  })
}

/**
 * Creates a themed confirmation prompt with consistent styling
 */
export const createThemedConfirm = (
  message: string,
  defaultValue: boolean = true
) => {
  return confirm({
    message: colors.reset(message),
    default: defaultValue,
    theme: {
      prefix: {
        done: colors.cyan("✔"),
        idle: "?",
      },
    },
  })
}

/**
 * Get the appropriate configuration file name based on package manager
 */
function getConfigFileName(packageManager: PackageManager): string {
  switch (packageManager) {
    case "npm":
      return ".npmrc (via npm config)"
    case "yarn":
      return ".yarnrc.yml or .npmrc (depending on Yarn version)"
    case "pnpm":
    case "bun":
      return ".npmrc"
    default:
      return "configuration file"
  }
}

/**
 * Display appropriate token configuration instructions for each package manager
 */
async function displayTokenInstructions(
  packageManager: PackageManager,
  token: string,
  cwd: string
) {
  logger.log(
    `To use this token, add it to your project's package manager configuration in ${colors.cyan(cwd)}:`
  )

  if (packageManager === "npm") {
    logger.log("Run these commands in your project directory:")
    logger.log(
      `  npm config set @tiptap-pro:registry https://registry.tiptap.dev/`
    )
    logger.log(`  npm config set //registry.tiptap.dev/:_authToken ${token}`)
    logger.log(`\nOr add to your project's .npmrc file:`)
    logger.log(`  @tiptap-pro:registry=https://registry.tiptap.dev/`)
    logger.log(`  //registry.tiptap.dev/:_authToken=${token}`)
  } else if (packageManager === "yarn") {
    const isYarnV1 = await checkYarnVersion(cwd)

    if (isYarnV1) {
      logger.log("Run these commands in your project directory:")
      logger.log(
        `  yarn config set @tiptap-pro:registry https://registry.tiptap.dev/`
      )
      logger.log(`  yarn config set //registry.tiptap.dev/:_authToken ${token}`)
    } else {
      logger.log(`Add to your project's .yarnrc.yml file:`)
      logger.log(
        `npmScopes:\n  tiptap-pro:\n    npmRegistryServer: "https://registry.tiptap.dev/"\n    npmAuthToken: "${token}"`
      )
    }
  } else if (packageManager === "pnpm" || packageManager === "bun") {
    logger.log(`Add to your project's .npmrc file:`)
    logger.log(
      colors.cyan(
        `@tiptap-pro:registry=https://registry.tiptap.dev/\n//registry.tiptap.dev/:_authToken=${token}`
      )
    )
  }
}

/**
 * Check if the project is using Yarn v1
 */
async function checkYarnVersion(cwd: string): Promise<boolean> {
  try {
    const yarnVersionOutput = await execa("yarn", ["--version"], { cwd })
    return yarnVersionOutput.stdout.startsWith("1.")
  } catch (error) {
    // If yarn command fails, default to false
    return false
  }
}

/**
 * Handle the login process with interactive prompts if needed
 */
export async function handleLogin(options: AuthOptions) {
  let { email, password, writeConfig, showMessage } = options
  const { cwd } = options

  // Interactive prompts if necessary values aren't provided
  if (!email) {
    email = await createThemedInput("Email:", {
      validate: (value: string) => (value ? true : "Please enter your email"),
    })
  }

  if (!password) {
    password = await createThemedPassword("Password:", {
      validate: (value: string) =>
        value ? true : "Please enter your password",
    })
  }

  // User cancelled the prompt
  if (!email || !password) {
    logger.error("Authentication cancelled")
    process.exit(0)
  }

  const packageManager = await getPackageManager(cwd)

  // Prompt for writeConfig if not explicitly set via command line
  if (writeConfig === undefined) {
    const configFileName = getConfigFileName(packageManager)

    writeConfig = await createThemedConfirm(
      `Would you like to save the auth token to your ${configFileName}?`
    )

    // User cancelled the prompt
    if (writeConfig === undefined) {
      logger.error("Authentication cancelled")
      process.exit(0)
    }
  }

  const result = await authenticateUser({
    email,
    password,
    packageManager,
    writeConfig: writeConfig ?? false,
    cwd,
  })

  if (result.success && result.tokens) {
    if (writeConfig && showMessage) {
      logger.log(
        `\nRegistry auth token saved to project directory for ${colors.cyan(packageManager)}`
      )
      logger.log(`.npmrc file saved at: ${colors.cyan(cwd)}`)
      logger.log(`Project path: ${colors.cyan(cwd)}`)
    } else if (result.tokens.authToken && showMessage) {
      logger.log(
        `\nRegistry Auth Token: ${colors.cyan(result.tokens.authToken)}`
      )
      logger.log("This token should be added to your project's configuration:")
      await displayTokenInstructions(
        packageManager,
        result.tokens.authToken,
        cwd
      )
    }

    if (showMessage) {
      logger.log(`Bearer token saved globally for CLI authentication`)
    }

    return true
  } else {
    logger.error("Authentication failed: " + result.error)

    return false
  }
}

/**
 * Handle checking authentication status
 */
async function handleStatusCheck(cwd: string) {
  const packageManager = await getPackageManager(cwd)
  const status = await checkAuthStatus()

  if (status.authenticated) {
    logger.success(
      `Authenticated as ${colors.cyan(status.user ?? "unknown user")}`
    )

    // Display plans
    if (status.plans && status.plans.length > 0) {
      if (status.plans.length === 1) {
        logger.log(`Plan: ${colors.cyan(status.plans[0])}`)
      } else {
        logger.log(`Plans: ${colors.cyan(status.plans.join(", "))}`)
      }
    } else {
      logger.log(`Plans: ${colors.cyan("No active plans")}`)
    }

    if (status.expires) {
      const expiryDate = new Date(status.expires)
      const now = new Date()
      const daysUntilExpiry = Math.ceil(
        (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      )

      if (daysUntilExpiry > 30) {
        logger.log(
          `Token expires: ${colors.cyan(expiryDate.toDateString())} (${daysUntilExpiry} days)`
        )
      } else if (daysUntilExpiry > 0) {
        logger.log(
          `Token expires: ${colors.yellow(expiryDate.toDateString())} (${daysUntilExpiry} days)`
        )
      } else {
        logger.log(`Token expires: ${colors.red("Expired")}`)
      }
    }

    if (status.token) {
      const tokenPreview = status.token.substring(0, 10) + "..."
      logger.log(`Bearer token: ${colors.cyan(tokenPreview)}`)
    }

    const registryToken = await getAuthToken(packageManager, cwd)
    if (registryToken) {
      const registryPreview = registryToken.substring(0, 10) + "..."
      logger.log(`Registry token: ${colors.cyan(registryPreview)}`)
    } else {
      logger.log(
        `Registry token: ${colors.yellow("Not configured for this project")}`
      )
      logger.log(
        `Run ${colors.cyan("@tiptap/cli login --write-config")} to configure for this project`
      )
    }
  } else {
    logger.log("Not authenticated with Tiptap registry")
    logger.log(`Run ${colors.cyan(`@tiptap/cli login`)} to authenticate`)
  }
}

export const login = new Command()
  .command("login")
  .description("log in to your Tiptap Cloud account")
  .option("-e, --email <email>", "your account email")
  .option("-p, --password <password>", "your account password")
  .option(
    "--write-config",
    "write the auth token to your package manager config",
    undefined
  )
  .option(
    "-c, --cwd <cwd>",
    "the working directory. defaults to the current directory.",
    process.cwd()
  )
  .action(async (options) => {
    try {
      const opts = authOptionsSchema.parse(options)
      await handleLogin(opts)
    } catch (error) {
      handleError(error)
    }
  })

export const status = new Command()
  .command("status")
  .description("review your Tiptap Cloud authentication status")
  .option(
    "-c, --cwd <cwd>",
    "the working directory. defaults to the current directory.",
    process.cwd()
  )
  .action(async (options) => {
    const opts = authOptionsSchema.parse(options)
    const { cwd } = opts
    try {
      await handleStatusCheck(cwd)
    } catch (error) {
      handleError(error)
    }
  })

export const logout = new Command()
  .command("logout")
  .description("log out from your Tiptap registry account")

  .action(async () => {
    try {
      const logoutSpinner = spinner("Logging out...")?.start()

      const result = await logoutUser()

      if (result.success) {
        logoutSpinner?.stopAndPersist({
          symbol: colors.cyan("✔"),
          text: "Successfully logged out",
        })
      } else {
        logoutSpinner?.fail("Logout failed")
        logger.error("Logout failed: " + result.error)
      }
    } catch (error) {
      handleError(error)
    }
  })

/**
 * Handle displaying license information
 */
async function handleLicenseInfo() {
  logger.log("")
  logger.log(colors.yellow("📋 Tiptap Pro License Information"))
  logger.log("")

  // Check license acceptance status
  const hasAccepted = tokenStorage.hasAcceptedLicense()
  const userInfo = tokenStorage.getUserInfo()

  if (hasAccepted) {
    logger.log(`${colors.cyan("✔")} Pro License and Terms of Service accepted`)
    if (userInfo.email) {
      logger.log(`  Accepted by: ${colors.cyan(userInfo.email)}`)
    }
    logger.log("")
  } else {
    logger.log(
      colors.yellow("⚠ Pro License and Terms of Service not yet accepted")
    )
    logger.log("  License acceptance is required for using paid components")
    logger.log("")
  }

  logger.log("License Details:")
  logger.log(
    `  • Pro License: ${colors.cyan("https://tiptap.dev/pro-license")}`
  )
  logger.log(
    `  • Terms of Service: ${colors.cyan("https://tiptap.dev/terms-of-service")}`
  )
  logger.log("")

  logger.log("About Paid Components:")
  logger.log(
    "  Some paid UI components or templates are limited to specific plans."
  )
  logger.log(
    "  The license is valid during your trial period and active subscription."
  )
  logger.log("")

  // Show hidden command hint only when using license command
  if (hasAccepted) {
    logger.log(colors.gray("Available commands:"))
    logger.log(
      colors.gray(
        `  • ${colors.cyan("tiptap license forget me")} - Reset license acceptance`
      )
    )
    logger.log("")
  }
}

/**
 * Handle license forget me command
 */
async function handleLicenseForgetMe() {
  const hasAccepted = tokenStorage.hasAcceptedLicense()

  if (!hasAccepted) {
    logger.log(colors.yellow("⚠ License has not been accepted yet"))
    return
  }

  const confirmed = await createThemedConfirm(
    "Are you sure you want to reset your license acceptance?",
    false
  )

  if (confirmed) {
    tokenStorage.clearLicenseAcceptance()
    logger.log(`${colors.cyan("✔")} License acceptance has been reset`)
    logger.log(
      "  You will need to accept the license again when installing paid components"
    )
  } else {
    logger.log("License acceptance reset cancelled")
  }
}

export const license = new Command()
  .command("license")
  .description("display Tiptap Pro license information and acceptance status")
  .action(async () => {
    try {
      await handleLicenseInfo()
    } catch (error) {
      handleError(error)
    }
  })
  .addCommand(
    new Command()
      .command("forget me")
      .description("reset license acceptance status")
      .action(async () => {
        try {
          await handleLicenseForgetMe()
        } catch (error) {
          handleError(error)
        }
      })
  )
